import { isGroup, } from './types.js';
/**
 * Wandelt eine Regel in einen deutschen Satz.
 *
 * Die Oberflaeche zeigt ihn unter jeder Bedingung. Wer einen Regelbaum nicht
 * lesen kann, soll trotzdem pruefen koennen, ob er das Richtige gebaut hat —
 * "hat Kampagne X in den letzten 14 Tagen nicht geoeffnet" versteht jeder,
 * eine verschachtelte Klammerstruktur nicht.
 */
const EVENT_LABELS = {
    sent: { verb: 'hat eine E-Mail erhalten', negated: 'hat keine E-Mail erhalten' },
    delivered: { verb: 'hat eine zugestellte E-Mail', negated: 'hat keine zugestellte E-Mail' },
    open: { verb: 'hat geöffnet', negated: 'hat nicht geöffnet' },
    click: { verb: 'hat geklickt', negated: 'hat nicht geklickt' },
    bounce_hard: { verb: 'war dauerhaft unzustellbar', negated: 'war nie dauerhaft unzustellbar' },
    bounce_soft: {
        verb: 'war vorübergehend unzustellbar',
        negated: 'war nie vorübergehend unzustellbar',
    },
    complaint: { verb: 'hat sich beschwert', negated: 'hat sich nie beschwert' },
    unsubscribe: { verb: 'hat sich abgemeldet', negated: 'hat sich nicht abgemeldet' },
    reply: { verb: 'hat geantwortet', negated: 'hat nicht geantwortet' },
    failed: { verb: 'konnte nicht versendet werden', negated: 'konnte immer versendet werden' },
};
const FIELD_LABELS = {
    email: 'E-Mail-Adresse',
    firstName: 'Vorname',
    lastName: 'Nachname',
    phone: 'Telefon',
    status: 'Status',
    source: 'Quelle',
    country: 'Land',
    city: 'Ort',
    leadScore: 'Lead-Score',
    lifecycleStage: 'Lebenszyklus',
    consentStatus: 'Einwilligung',
    createdAt: 'Anlagedatum',
    lastActivityAt: 'letzte Aktivität',
};
const OPERATOR_LABELS = {
    equals: 'ist',
    not_equals: 'ist nicht',
    contains: 'enthält',
    not_contains: 'enthält nicht',
    starts_with: 'beginnt mit',
    ends_with: 'endet auf',
    in: 'ist eines von',
    not_in: 'ist keines von',
    is_empty: 'ist leer',
    is_not_empty: 'ist ausgefüllt',
    gt: 'ist größer als',
    gte: 'ist mindestens',
    lt: 'ist kleiner als',
    lte: 'ist höchstens',
    between: 'liegt zwischen',
};
const UNIT_LABELS = {
    hours: 'Stunden',
    days: 'Tagen',
    weeks: 'Wochen',
    months: 'Monaten',
};
export function describeNode(node, context = {}) {
    if (isGroup(node)) {
        const children = node.children ?? [];
        if (children.length === 0)
            return 'alle Kontakte';
        const joiner = node.op === 'OR' ? ' ODER ' : ' UND ';
        return children.map((child) => describeNode(child, context)).join(joiner);
    }
    return describeCondition(node, context);
}
function describeCondition(condition, context) {
    switch (condition.type) {
        case 'email_event': {
            const labels = EVENT_LABELS[condition.event];
            const parts = [condition.negate ? labels.negated : labels.verb];
            if (condition.scope?.campaignId) {
                const name = context.campaignNames?.[condition.scope.campaignId] ?? 'eine Kampagne';
                parts.splice(0, 0, `Kampagne „${name}"`);
            }
            if (condition.scope?.journeyNodeId) {
                parts.push(`in Schritt „${condition.scope.journeyNodeId}"`);
            }
            if (condition.urlContains)
                parts.push(`auf einen Link mit „${condition.urlContains}"`);
            if (condition.timeframe)
                parts.push(describeTimeframe(condition.timeframe));
            if (condition.count)
                parts.push(describeCount(condition.count));
            if (condition.uniqueOnly)
                parts.push('(nur eindeutige Ereignisse)');
            return parts.join(' ');
        }
        case 'attribute': {
            const field = FIELD_LABELS[condition.field] ?? condition.field;
            return `${field} ${describeComparison(condition.op, condition.value, condition.value2, condition.values)}`;
        }
        case 'custom_field':
            return `Feld „${condition.key}" ${describeComparison(condition.op, condition.value, undefined, condition.values)}`;
        case 'custom_event': {
            const base = condition.negate
                ? `Ereignis „${condition.name}" ist nie eingetreten`
                : `Ereignis „${condition.name}" ist eingetreten`;
            const parts = [base];
            if (condition.timeframe)
                parts.push(describeTimeframe(condition.timeframe));
            if (condition.count)
                parts.push(describeCount(condition.count));
            if (condition.aggregate) {
                const fnLabel = { sum: 'Summe', avg: 'Durchschnitt', max: 'Maximum', min: 'Minimum' }[condition.aggregate.fn];
                parts.push(`${fnLabel} ${condition.aggregate.op} ${condition.aggregate.value}`);
            }
            return parts.join(' ');
        }
        case 'list_member': {
            const name = context.listNames?.[condition.listId] ?? 'einer Liste';
            return condition.negate ? `ist nicht in Liste „${name}"` : `ist in Liste „${name}"`;
        }
        case 'tag': {
            const name = context.tagNames?.[condition.tagId] ?? 'einem Tag';
            return condition.negate ? `hat nicht das Tag „${name}"` : `hat das Tag „${name}"`;
        }
        case 'in_segment': {
            const name = context.segmentNames?.[condition.segmentId] ?? 'einem Segment';
            return condition.negate ? `ist nicht im Segment „${name}"` : `ist im Segment „${name}"`;
        }
        default:
            return 'unbekannte Bedingung';
    }
}
function describeTimeframe(timeframe) {
    switch (timeframe.kind) {
        case 'last':
            return `in den letzten ${timeframe.value} ${UNIT_LABELS[timeframe.unit] ?? timeframe.unit}`;
        case 'older_than':
            return `vor mehr als ${timeframe.value} ${UNIT_LABELS[timeframe.unit] ?? timeframe.unit}`;
        case 'before':
            return `vor dem ${formatDate(timeframe.date)}`;
        case 'after':
            return `nach dem ${formatDate(timeframe.date)}`;
        case 'between':
            return `zwischen dem ${formatDate(timeframe.from)} und dem ${formatDate(timeframe.to)}`;
        default:
            return '';
    }
}
function describeCount(count) {
    if (count.op === 'between')
        return `(${count.value} bis ${count.value2 ?? count.value} Mal)`;
    const labels = {
        '=': 'genau',
        '>': 'mehr als',
        '>=': 'mindestens',
        '<': 'weniger als',
        '<=': 'höchstens',
    };
    return `(${labels[count.op] ?? count.op} ${count.value} Mal)`;
}
function describeComparison(op, value, value2, values) {
    const label = OPERATOR_LABELS[op] ?? op;
    if (op === 'is_empty' || op === 'is_not_empty')
        return label;
    if (op === 'between')
        return `${label} ${String(value)} und ${String(value2)}`;
    if (op === 'in' || op === 'not_in')
        return `${label} ${(values ?? []).join(', ')}`;
    return `${label} „${String(value)}"`;
}
function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('de-CH');
}
//# sourceMappingURL=describe.js.map